import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useDispatch } from 'react-redux';
import { fetchPatientDetails } from '../../redux/slices/patientsSlice';
import allergyService from '../../services/allergyService';
import PatientAllergiesService from '../../services/patientAllergiesService';

// Styled Components - Modern Theme (matching CreateEncounter)
const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1.5, 3),
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
  color: 'white',
  borderRadius: '16px 16px 0 0',
  '& .MuiTypography-root': {
    color: 'white',
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
}));

const AllergiesTab = ({ patient }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [newAllergies, setNewAllergies] = useState([{ name: '', reaction: '', severity: 'mild' }]);
  const [allergiesMetadata, setAllergiesMetadata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filteredAllergies, setFilteredAllergies] = useState([]);
  const dispatch = useDispatch();

  // Load allergies metadata when component mounts
  useEffect(() => {
    loadAllergiesMetadata();
  }, []);

  // Filter allergies based on search term
  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = allergiesMetadata.filter(allergy =>
        allergy.drugName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        allergy.allergyCategories.some(cat => 
          cat.category.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredAllergies(filtered);
    } else {
      setFilteredAllergies(allergiesMetadata);
    }
    setPage(0);
  }, [searchTerm, allergiesMetadata]);

  const loadAllergiesMetadata = async () => {
    setLoading(true);
    try {
      const data = await allergyService.getAllAllergyMetadata(1000);
      setAllergiesMetadata(data);
      setFilteredAllergies(data);
    } catch (error) {
      console.error('Error loading allergies metadata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setNewAllergies([{ name: '', reaction: '', severity: 'mild' }]);
    setSearchTerm('');
  };

  const handleAddAllergyRow = () => {
    const safeAllergies = Array.isArray(newAllergies) ? newAllergies : [];
    setNewAllergies([...safeAllergies, { name: '', reaction: '', severity: 'mild' }]);
  };

  const handleRemoveAllergyRow = (index) => {
    const safeAllergies = Array.isArray(newAllergies) ? newAllergies : [];
    if (safeAllergies.length > 1) {
      const updated = safeAllergies.filter((_, i) => i !== index);
      setNewAllergies(updated);
    }
  };

  const handleAllergyChange = (index, field, value) => {
    const safeAllergies = Array.isArray(newAllergies) ? newAllergies : [];
    const updated = [...safeAllergies];
    if (updated[index]) {
      updated[index][field] = value;
      setNewAllergies(updated);
    }
  };

  const handleSave = async () => {
    if (!patient?.id) return;

    // Filter out empty allergies
    const safeAllergies = Array.isArray(newAllergies) ? newAllergies : [];
    const validAllergies = safeAllergies.filter(allergy => allergy.name && allergy.name.trim());
    
    if (validAllergies.length === 0) return;

    try {
      // Use service layer to update patient allergies
              const allergiesService = new PatientAllergiesService();
        await allergiesService.updatePatientAllergies(patient.id, validAllergies);
      
      dispatch(fetchPatientDetails(patient.id));
      handleClose();
    } catch (error) {
      console.error('Error saving allergies:', error);
      alert('Failed to save allergies. Please try again.');
    }
  };

  const handleAddFromMetadata = (allergyMetadata) => {
    const safeAllergies = Array.isArray(newAllergies) ? newAllergies : [];
    const newAllergy = {
      name: allergyMetadata.drugName,
      reaction: allergyMetadata.allergyCategories[0]?.commonReactions[0] || 'Allergic reaction',
      severity: allergyMetadata.riskLevel === 'high' ? 'severe' : 
                allergyMetadata.riskLevel === 'medium' ? 'moderate' : 'mild'
    };
    
    setNewAllergies([...safeAllergies, newAllergy]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'severe': return 'error';
      case 'moderate': return 'warning';
      case 'mild': return 'success';
      default: return 'default';
    }
  };

  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Allergies
        </Typography>
        <ModernButton 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={handleOpen}
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
            },
          }}
        >
          Add Allergy
        </ModernButton>
      </Box>

      <ModernCard>
        <CardContent sx={{ p: 3 }}>
          {Array.isArray(patient?.allergies) && patient.allergies.length > 0 ? (
            <List sx={{ p: 0 }}>
              {patient.allergies.map((allergy, index) => (
                <ListItem 
                  key={index} 
                  divider 
                  sx={{ 
                    borderRadius: '8px', 
                    mb: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                        {allergy.name}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        Reaction: {allergy.reaction || 'N/A'} - Severity: {allergy.severity || 'N/A'}
                      </Typography>
                    }
                  />
                  <Chip 
                    label={allergy.severity || 'Unknown'} 
                    color={getSeverityColor(allergy.severity)}
                    size="small"
                    sx={{ 
                      borderRadius: '8px',
                      fontWeight: 600,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              color: theme.palette.text.secondary 
            }}>
              <Typography variant="body1">
                No allergies recorded for this patient
              </Typography>
            </Box>
          )}
        </CardContent>
      </ModernCard>

      <StyledDialog 
        open={open} 
        onClose={handleClose} 
        fullWidth 
        maxWidth="lg"
        PaperProps={{
          sx: { height: '80vh', maxHeight: '80vh' }
        }}
      >
        <StyledDialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Add Patient Allergies
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Add new allergies for {patient?.name}
          </Typography>
        </StyledDialogTitle>
        
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Top Panel - Add Allergies */}
          <StyledPaper sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                Add New Allergies
              </Typography>
              <ModernButton
                variant="outlined"
                startIcon={<AddCircleIcon />}
                onClick={handleAddAllergyRow}
                size="small"
                sx={{
                  borderRadius: '8px',
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.dark,
                    backgroundColor: theme.palette.primary.light + '10',
                  },
                }}
              >
                Add Row
              </ModernButton>
            </Box>
            
            {(Array.isArray(newAllergies) ? newAllergies : []).map((allergy, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <StyledTextField
                      fullWidth
                      label="Allergy Name"
                      value={allergy.name}
                      onChange={(e) => handleAllergyChange(index, 'name', e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StyledTextField
                      fullWidth
                      label="Reaction"
                      value={allergy.reaction}
                      onChange={(e) => handleAllergyChange(index, 'reaction', e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Severity</InputLabel>
                      <Select
                        value={allergy.severity}
                        onChange={(e) => handleAllergyChange(index, 'severity', e.target.value)}
                        label="Severity"
                        sx={{
                          borderRadius: '12px',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: '12px',
                          },
                        }}
                      >
                        <MenuItem value="mild">Mild</MenuItem>
                        <MenuItem value="moderate">Moderate</MenuItem>
                        <MenuItem value="severe">Severe</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={1}>
                    {(Array.isArray(newAllergies) ? newAllergies : []).length > 1 && (
                      <IconButton
                        onClick={() => handleRemoveAllergyRow(index)}
                        color="error"
                        size="small"
                        sx={{
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: theme.palette.error.light + '20',
                          },
                        }}
                      >
                        <AddIcon sx={{ transform: 'rotate(45deg)' }} />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              </Box>
            ))}
          </StyledPaper>

          {/* Bottom Panel - Allergies Metadata */}
          <StyledPaper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Allergies Database
                </Typography>
                <StyledTextField
                  size="small"
                  placeholder="Search allergies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                  sx={{ width: 300 }}
                />
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: theme.palette.background.default }}>
                        <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Drug Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Categories</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Risk Level</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Common Reactions</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAllergies
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((allergy) => (
                          <TableRow 
                            key={allergy.id} 
                            hover
                            sx={{
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                {allergy.drugName}
                              </Typography>
                              {allergy.rxnormCUI && (
                                <Typography variant="caption" color="text.secondary">
                                  CUI: {allergy.rxnormCUI}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {allergy.allergyCategories.slice(0, 2).map((cat, idx) => (
                                  <Chip
                                    key={idx}
                                    label={cat.category}
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderRadius: '6px' }}
                                  />
                                ))}
                                {allergy.allergyCategories.length > 2 && (
                                  <Chip
                                    label={`+${allergy.allergyCategories.length - 2}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderRadius: '6px' }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={allergy.riskLevel.toUpperCase()}
                                color={getRiskLevelColor(allergy.riskLevel)}
                                size="small"
                                sx={{ borderRadius: '6px', fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}>
                                {allergy.allergyCategories[0]?.commonReactions.slice(0, 2).join(', ')}
                                {allergy.allergyCategories[0]?.commonReactions.length > 2 && '...'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Add to patient allergies">
                                <IconButton
                                  size="small"
                                  onClick={() => handleAddFromMetadata(allergy)}
                                  color="primary"
                                  sx={{
                                    borderRadius: '8px',
                                    '&:hover': {
                                      backgroundColor: theme.palette.primary.light + '20',
                                    },
                                  }}
                                >
                                  <AddCircleIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <TablePagination
              component="div"
              count={filteredAllergies.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  color: theme.palette.text.secondary,
                },
              }}
            />
          </StyledPaper>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
          <ModernButton onClick={handleClose} variant="outlined">
            Cancel
          </ModernButton>
          <ModernButton 
            onClick={handleSave} 
            variant="contained"
            disabled={!(Array.isArray(newAllergies) ? newAllergies : []).some(allergy => allergy.name && allergy.name.trim())}
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              },
              '&:disabled': {
                background: theme.palette.action.disabledBackground,
              },
            }}
          >
            Save Allergies
          </ModernButton>
        </DialogActions>
      </StyledDialog>
    </Box>
  );
};

export default AllergiesTab; 